// services/blogServices.js
const STORAGE_KEY = 'blog_posts';

// Helper: compress image before saving as Base64
const compressImage = (base64, maxWidth = 800, quality = 0.6) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      const compressed = canvas.toDataURL('image/jpeg', quality);
      resolve(compressed);
    };
    img.onerror = () => resolve(base64); // fallback
  });
};

const getBlogs = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored !== 'undefined' && stored !== 'null') {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to parse blogs', e);
  }
  return [];
};

const setBlogs = (blogs) => {
  try {
    if (!Array.isArray(blogs)) throw new Error('Blogs must be an array');
    localStorage.setItem(STORAGE_KEY, JSON.stringify(blogs));
    return true;
  } catch (e) {
    if (e.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded – data too large');
      return { error: 'Storage limit reached. Please delete some old blogs or use smaller images.' };
    }
    console.error('Failed to save blogs', e);
    return false;
  }
};

let listeners = [];

const notifyListeners = () => {
  const allBlogs = getBlogs();
  const stats = {
    total: allBlogs.length,
    published: allBlogs.filter(b => b.status === 'published').length,
    drafts: allBlogs.filter(b => b.status === 'draft').length,
    categories: [...new Set(allBlogs.map(b => b.category))].length
  };
  listeners.forEach(cb => {
    try { cb({ allBlogs, stats }); } catch (e) { console.error(e); }
  });
};

// Public API
export const getAllBlogs = () => getBlogs();
export const getPublishedBlogs = () => getAllBlogs().filter(b => b.status === 'published');
export const getDraftBlogs = () => getAllBlogs().filter(b => b.status === 'draft');
export const getBlogById = id => getAllBlogs().find(b => b.id === id);

export const saveBlog = async (blogData) => {
  // Validation
  if (!blogData.title?.trim()) return { success: false, error: 'Title is required' };
  if (!blogData.category) return { success: false, error: 'Category is required' };
  if (!blogData.content?.trim()) return { success: false, error: 'Content is required' };
  if (blogData.content.trim().length < 50) return { success: false, error: 'Content must be at least 50 characters' };
  if (!blogData.publishDate) return { success: false, error: 'Publish date is required' };

  // Compress image if it's a Base64 string (user upload)
  let finalImage = blogData.image;
  if (finalImage && finalImage.startsWith('data:image') && finalImage.length > 100000) {
    try {
      finalImage = await compressImage(finalImage);
    } catch (e) {
      console.warn('Image compression failed, using placeholder');
      finalImage = 'https://via.placeholder.com/800x400?text=Image+Error';
    }
  } else if (!finalImage) {
    finalImage = `https://via.placeholder.com/800x400?text=${encodeURIComponent(blogData.title.substring(0, 20))}`;
  }

  const blogs = getBlogs();
  const now = new Date().toISOString();

  try {
    if (blogData.id) {
      const index = blogs.findIndex(b => b.id === blogData.id);
      if (index === -1) return { success: false, error: 'Blog not found' };
      blogs[index] = {
        ...blogs[index],
        title: blogData.title.trim(),
        category: blogData.category,
        content: blogData.content.trim(),
        publishDate: blogData.publishDate,
        image: finalImage,
        status: blogData.status,
        author: blogData.author,
        updatedAt: now
      };
    } else {
      const newBlog = {
        id: Date.now().toString(),
        title: blogData.title.trim(),
        category: blogData.category,
        content: blogData.content.trim(),
        publishDate: blogData.publishDate,
        image: finalImage,
        status: blogData.status || 'draft',
        author: blogData.author || 'unknown',
        createdAt: now,
        updatedAt: now
      };
      blogs.unshift(newBlog);
    }

    const saveResult = setBlogs(blogs);
    if (saveResult === false) {
      return { success: false, error: 'Failed to save to localStorage – possibly quota exceeded.' };
    }
    if (saveResult?.error) {
      return { success: false, error: saveResult.error };
    }
    notifyListeners();
    return { success: true, blog: blogData.id ? blogs.find(b => b.id === blogData.id) : blogs[0] };
  } catch (err) {
    console.error('saveBlog error:', err);
    return { success: false, error: err.message };
  }
};

export const deleteBlog = (id) => {
  try {
    const blogs = getBlogs();
    const newBlogs = blogs.filter(b => b.id !== id);
    if (newBlogs.length === blogs.length) return { success: false, error: 'Blog not found' };
    const saved = setBlogs(newBlogs);
    if (saved === false) return { success: false, error: 'Failed to save after deletion' };
    notifyListeners();
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const updateBlogStatus = (id, status) => {
  if (!['published', 'draft'].includes(status)) return { success: false, error: 'Invalid status' };
  try {
    const blogs = getBlogs();
    const blog = blogs.find(b => b.id === id);
    if (!blog) return { success: false, error: 'Blog not found' };
    blog.status = status;
    blog.updatedAt = new Date().toISOString();
    const saved = setBlogs(blogs);
    if (saved === false) return { success: false, error: 'Failed to save status change' };
    notifyListeners();
    return { success: true, blog };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const publishBlog = id => updateBlogStatus(id, 'published');
export const saveAsDraft = id => updateBlogStatus(id, 'draft');

export const getBlogStats = () => {
  const blogs = getAllBlogs();
  return {
    total: blogs.length,
    published: blogs.filter(b => b.status === 'published').length,
    drafts: blogs.filter(b => b.status === 'draft').length,
    categories: [...new Set(blogs.map(b => b.category))].length
  };
};

export const addListener = (callback) => {
  listeners.push(callback);
  return () => { listeners = listeners.filter(cb => cb !== callback); };
};

// Seed sample data only if empty
export const seedSampleData = () => {
  const existing = getBlogs();
  if (existing.length === 0) {
    const sample = [
      {
        id: 'sample_1',
        title: 'Welcome to Our Blog',
        category: 'Stories',
        content: 'This is a sample blog post. Edit or delete it. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
        publishDate: new Date().toISOString().split('T')[0],
        image: 'https://via.placeholder.com/800x400?text=Welcome',
        status: 'published',
        author: 'admin@example.com',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'sample_2',
        title: 'Getting Started with Blogging',
        category: 'Newsroom',
        content: 'Learn how to create amazing blog posts with our easy-to-use editor. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
        publishDate: new Date().toISOString().split('T')[0],
        image: 'https://via.placeholder.com/800x400?text=Getting+Started',
        status: 'draft',
        author: 'admin@example.com',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ];
    setBlogs(sample);
    notifyListeners();
  }
};

export default {
  getAllBlogs,
  getPublishedBlogs,
  getDraftBlogs,
  getBlogById,
  saveBlog,
  deleteBlog,
  updateBlogStatus,
  publishBlog,
  saveAsDraft,
  getBlogStats,
  addListener,
  seedSampleData
};